import { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
} from "recharts";
import {
  TrendingUp,
  Target,
  ShoppingCart,
  Percent,
  Award,
  CheckCircle2,
  Truck,
  Receipt,
  Wallet,
  Send,
} from "lucide-react";
import api from "../../lib/api";
import { eur, pct, famColor } from "../../lib/format";
import { useViewRole } from "../../context/ViewRoleContext";
import { ChartTooltip } from "../../lib/ChartTooltip";
import { ScopeSwitcher } from "../common/ScopeSwitcher";

const PERIODES = ["Ce mois", "Trimestre", "Année", "Tout"];

function famLabel(f) {
  return f.length > 16 ? f.slice(0, 15) + "…" : f;
}

export default function AnalyticsModule() {
  const { canSeeMargins, isAdmin } = useViewRole();
  const [data, setData] = useState(null);
  const [famDefs, setFamDefs] = useState([]);
  const [periode, setPeriode] = useState("Année");
  const [scope, setScope] = useState("real");

  useEffect(() => { api.get("/familles").then((r) => setFamDefs(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get(`/analytics?periode=${encodeURIComponent(periode)}&scope=${scope}`).then((r) => setData(r.data)).catch(() => {});
  }, [periode, scope]);

  if (!data) return <div className="text-slate-500">Chargement des analyses…</div>;

  const familles = data.familles.map((f) => ({ ...f, court: famLabel(f.famille) }));

  return (
    <div className="space-y-6" data-testid="analytics-view">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-head text-3xl font-extrabold tracking-tight">Module d'Analyse Poussée</h1>
            <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">Expert</span>
          </div>
          <p className="text-slate-400 text-sm mt-1">Moyennes, validations par famille et distribution des marges.</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && <ScopeSwitcher scope={scope} setScope={setScope} />}
          <div className="flex gap-1 p-1 rounded-lg bg-[#111827] border border-[#1E293B]">
          {PERIODES.map((p) => (
            <button key={p} data-testid={`analytics-periode-${p}`} onClick={() => setPeriode(p)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${periode === p ? "bg-amber-500 text-[#0B0F17]" : "text-slate-400 hover:text-slate-200"}`}>
              {p}
            </button>
          ))}
          </div>
        </div>
      </div>

      {/* ================= PARCOURS COMMERCIAL ================= */}
      <div>
        <div className="flex items-end justify-between mb-3">
          <div>
            <h2 className="font-head text-lg font-bold">Parcours commercial</h2>
            <p className="text-xs text-slate-500">
              Création → acceptation → commande → livraison → facturation → encaissement
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <StageStat
            icon={Target}
            color="#38BDF8"
            title="Potentiel"
            value={eur(data.ca_potentiel_ht)}
            sub="pipeline ouvert"
          />
          <StageStat
            icon={CheckCircle2}
            color="#10B981"
            title="Signé"
            value={eur(data.ca_signe_ht)}
            sub="accepté"
          />
          <StageStat
            icon={ShoppingCart}
            color="#F59E0B"
            title="Commandé"
            value={eur(data.ca_commande_ht)}
            sub="commande passée"
          />
          <StageStat
            icon={Truck}
            color="#A78BFA"
            title="Livré"
            value={eur(data.ca_livre_ht)}
            sub="livraison effectuée"
          />
          <StageStat
            icon={Receipt}
            color="#FB7185"
            title="Facturé"
            value={eur(data.ca_facture_ht)}
            sub="facturation effectuée"
          />
          <StageStat
            icon={Wallet}
            color="#22C55E"
            title="Encaissé"
            value={eur(data.ca_encaisse_ht)}
            sub="paiement reçu"
          />
        </div>
      </div>

      {/* ================= INDICATEURS COMMERCIAUX ================= */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat
          icon={ShoppingCart}
          color="#38BDF8"
          title="Panier moyen HT"
          value={eur(data.panier_moyen_ht)}
          sub="par dossier signé"
        />
        <Stat
          icon={Percent}
          color="#10B981"
          title="Taux de transformation"
          value={pct(data.taux_conversion_pct)}
          sub={`${data.nb_valides}/${data.nb_devis} dossiers signés`}
        />
        <Stat
          icon={Target}
          color="#F59E0B"
          title="Dossiers analysés"
          value={data.nb_devis}
          sub="sur la période"
        />
        <Stat
          icon={Award}
          color="#A78BFA"
          title="Dossiers signés"
          value={data.nb_valides}
          sub="acceptés / signés"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Validations par famille de prestations" subtitle="Nombre présentées vs validées">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={familles} margin={{ left: -10, right: 8, top: 8 }}>
              <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="court" stroke="#64748B" fontSize={10} tickLine={false} axisLine={false} interval={0} angle={-15} textAnchor="end" height={50} />
              <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(148,163,184,0.06)" }} />
              <Legend wrapperStyle={{ fontSize: 12, color: "#94A3B8" }} />
              <Bar dataKey="presentes" name="Présentées" fill="#334155" radius={[4, 4, 0, 0]} />
              <Bar dataKey="validees" name="Validées" fill="#F59E0B" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Taux d'acceptation par famille" subtitle="Où gagne-t-on le plus de dossiers ?">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={familles} layout="vertical" margin={{ left: 10, right: 20 }}>
              <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}%`} />
              <YAxis type="category" dataKey="court" stroke="#64748B" fontSize={10} tickLine={false} axisLine={false} width={110} />
              <Tooltip content={<ChartTooltip formatter={(v) => `${v} %`} />} cursor={{ fill: "rgba(148,163,184,0.06)" }} />
              <Bar dataKey="taux_acceptation_pct" name="Acceptation" radius={[0, 4, 4, 0]}>
                {familles.map((f) => <Cell key={f.famille} fill={famColor(f.famille, famDefs)} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {canSeeMargins && (
          <Card title="Distribution de la marge par famille" subtitle="Répartition du profit généré (€)">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={familles} margin={{ left: -10, right: 8, top: 8 }}>
                <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="court" stroke="#64748B" fontSize={10} tickLine={false} axisLine={false} interval={0} angle={-15} textAnchor="end" height={50} />
                <YAxis stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                <Tooltip content={<ChartTooltip formatter={eur} />} cursor={{ fill: "rgba(148,163,184,0.06)" }} />
                <Bar dataKey="marge_totale" name="Marge" fill="#10B981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}

        <Card
          title="Parcours commercial — évolution"
          subtitle="Évolution mensuelle des différentes étapes du cycle de vente"
        >
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={data.evolution_c2 || data.evolution} margin={{ left: -10, right: 8, top: 8 }}>
              <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="mois"
                stroke="#64748B"
                fontSize={11}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke="#64748B"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => `${Math.round(v / 1000)}k`}
              />
              <Tooltip content={<ChartTooltip formatter={eur} />} />
              <Legend wrapperStyle={{ fontSize: 11, color: "#94A3B8" }} />

              <Line
                type="monotone"
                dataKey="ca_potentiel"
                name="Potentiel"
                stroke="#38BDF8"
                strokeWidth={2}
                dot={{ r: 2 }}
              />
              <Line
                type="monotone"
                dataKey="ca_signe"
                name="Signé"
                stroke="#10B981"
                strokeWidth={2}
                dot={{ r: 2 }}
              />
              <Line
                type="monotone"
                dataKey="ca_commande"
                name="Commandé"
                stroke="#F59E0B"
                strokeWidth={2}
                dot={{ r: 2 }}
              />
              <Line
                type="monotone"
                dataKey="ca_livre"
                name="Livré"
                stroke="#A78BFA"
                strokeWidth={2}
                dot={{ r: 2 }}
              />
              <Line
                type="monotone"
                dataKey="ca_facture"
                name="Facturé"
                stroke="#FB7185"
                strokeWidth={2}
                dot={{ r: 2 }}
              />
              <Line
                type="monotone"
                dataKey="ca_encaisse"
                name="Encaissé"
                stroke="#22C55E"
                strokeWidth={2}
                dot={{ r: 2 }}
              />

              {canSeeMargins && (
                <Line
                  type="monotone"
                  dataKey="marge_signee"
                  name="Marge signée"
                  stroke="#F97316"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ r: 2 }}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card title="Rentabilité par chantier" subtitle="CA facturé face à la marge réelle">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-[#1E293B]">
                <th className="px-3 py-2.5 font-medium">Chantier</th>
                <th className="px-3 py-2.5 font-medium text-right">CA facturé</th>
                {canSeeMargins && <th className="px-3 py-2.5 font-medium text-right">Marge réelle</th>}
                {canSeeMargins && <th className="px-3 py-2.5 font-medium text-right">Taux</th>}
                <th className="px-3 py-2.5 font-medium w-1/3">Performance</th>
              </tr>
            </thead>
            <tbody>
              {data.rentabilite_chantiers.map((c) => (
                <tr key={c.code} className="border-b border-[#1E293B]/60" data-testid={`analytics-chantier-${c.code}`}>
                  <td className="px-3 py-2.5">
                    <span className="font-mono-metric text-xs text-slate-500 mr-2">{c.code}</span>{c.titre}
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono-metric text-sky-400">{eur(c.ca)}</td>
                  {canSeeMargins && <td className="px-3 py-2.5 text-right font-mono-metric text-emerald-400">{eur(c.marge)}</td>}
                  {canSeeMargins && <td className="px-3 py-2.5 text-right font-mono-metric font-semibold">{pct(c.taux)}</td>}
                  <td className="px-3 py-2.5">
                    <div className="h-2 rounded-full bg-[#0B0F17] overflow-hidden">
                      <div className={`h-full rounded-full ${c.taux >= 35 ? "bg-emerald-500" : c.taux >= 20 ? "bg-amber-500" : "bg-red-500"}`} style={{ width: `${Math.max(3, Math.min(100, c.taux * 2))}%` }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function StageStat({ icon: Icon, color, title, value, sub }) {
  return (
    <div className="rounded-xl bg-[#111827] border border-[#1E293B] p-4">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
          {title}
        </span>
        <Icon className="h-4 w-4" style={{ color }} />
      </div>
      <div className="mt-2 font-mono-metric text-lg font-bold">
        {value}
      </div>
      <div className="mt-1 text-[10px] text-slate-500">
        {sub}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, color, title, value, sub }) {
  return (
    <div className="rounded-xl bg-[#111827] border border-[#1E293B] p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">{title}</span>
        <Icon className="h-4 w-4" style={{ color }} />
      </div>
      <div className="mt-3 font-mono-metric text-2xl font-bold">{value}</div>
      <div className="mt-1 text-xs text-slate-500">{sub}</div>
    </div>
  );
}

function Card({ title, subtitle, children }) {
  return (
    <div className="rounded-xl bg-[#111827] border border-[#1E293B] p-5">
      <div className="mb-4">
        <h2 className="font-head text-lg font-bold">{title}</h2>
        {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}
